import React from 'react';
import { Navbar } from '../../components/Navbar';
import { useTranslation } from 'react-i18next';

import AboutUsBG from '../../assets/AboutUsBG.jpg';
import SliStructureDiagramEN from '../../assets/SLI-structure-diagram.svg';
import SliStructureDiagramES from '../../assets/SLI-structure-diagram-Spanish.svg';
import ProjectProcessTraditionalDiagramEN from '../../assets/project-process-traditional.svg';
import ProjectProcessTraditionalDiagramES from '../../assets/project-process-traditional-Spanish.svg';
import ProjectProcessSliDiagramEN from '../../assets/project-process-SLI.svg';
import ProjectProcessSliDiagramES from '../../assets/project-process-Spanish-SLI.svg';
import SLI_capacityEN from '../../assets/SLI_capacity.svg';
import SLI_capacityES from '../../assets/SLI_capacity-Spanish.svg';
import Aggregation from '../../assets/Aggregation.svg';
import { PictureContentRight } from '../../components/PictureContentRight';
import { PictureContentLeft } from '../../components/PictureContentLeft';
import { ContentBox } from '../../components/ContentBox';
import { ArrowRight } from 'lucide-react';

const AboutUsPage = () => {
  const { t, i18n } = useTranslation();

  // Mapping object for all localized images
  const images = {
    sliStructure: {
      en: SliStructureDiagramEN,
      es: SliStructureDiagramES,
    },
    projectProcessTraditional: {
      en: ProjectProcessTraditionalDiagramEN,
      es: ProjectProcessTraditionalDiagramES,
    },
    projectProcessSli: {
      en: ProjectProcessSliDiagramEN,
      es: ProjectProcessSliDiagramES,
    },
    sliCapacity: {
      en: SLI_capacityEN,
      es: SLI_capacityES,
    },
  };

  // Helper function to get the correct image based on current language
  const getImage = (imageKey) => {
    return images[imageKey][i18n.language] || images[imageKey].en;
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-full flex-col bg-white">
      <Navbar
        src={AboutUsBG}
        pageName={t('aboutUs.navbar.pageName')}
        pageText={t('aboutUs.navbar.pageText')}
      />
      <ContentBox
        title=""
        content={
          <div className="flex flex-col gap-10 md:grid md:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] md:items-start md:gap-16">
            <div className="w-full md:max-w-[460px]">
              <p className="text-left text-lg leading-8">
                {t('aboutUs.sections.intro.text1')}&nbsp;
                <a
                  href={t('aboutUs.sections.intro.link1Url')}
                  target="partner"
                  className="text-lime-700 hover:text-emerald-700 hover:underline"
                >
                  {t('aboutUs.sections.intro.link1Text')}
                </a>
                &nbsp;{t('aboutUs.sections.intro.text2')}&nbsp;
                <a
                  href={t('aboutUs.sections.intro.link2Url')}
                  target="partner"
                  className="text-lime-700 hover:text-emerald-700 hover:underline"
                >
                  {t('aboutUs.sections.intro.link2Text')}
                </a>
                &nbsp;{t('aboutUs.sections.intro.text3')}&nbsp;
                <a
                  href={t('aboutUs.sections.intro.link3Url')}
                  target="partner"
                  className="text-lime-700 hover:text-emerald-700 hover:underline"
                >
                  {t('aboutUs.sections.intro.link3Text')}
                </a>
                {t('aboutUs.sections.intro.text4')}
              </p>
            </div>
            <div className="flex w-full justify-center md:justify-end">
              <img
                className="w-full max-w-[520px] rounded-md md:max-w-[560px] lg:max-w-[620px]"
                src={getImage('sliStructure')}
                alt="The collaborative structure of SLI."
              />
            </div>
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
        textAlign={'text-left'}
      />
      <ContentBox
        title={t('aboutUs.sections.howItWorks.title')}
        content={
          <div>
            {t('aboutUs.sections.howItWorks.text1')}
            <img
              className={`w-[800px] mx-auto my-4`}
              src={getImage('projectProcessTraditional')}
              alt="The traditional process of project development for Conservation Districts."
            />
            <p>
              {t('aboutUs.sections.howItWorks.text2Part1')}{' '}
              <strong>{t('aboutUs.sections.howItWorks.text2Strong1')}</strong>{' '}
              {t('aboutUs.sections.howItWorks.text2Part2')}{' '}
              <strong>{t('aboutUs.sections.howItWorks.text2Strong2')}</strong>{' '}
              {t('aboutUs.sections.howItWorks.text2Part3')}
            </p>
            <img
              className={`w-[800px] mx-auto my-4`}
              src={getImage('projectProcessSli')}
              alt="The traditional process of project development for Conservation Districts."
            />
            <p>
              {t('aboutUs.sections.howItWorks.text3Part1')}{' '}
              <strong>{t('aboutUs.sections.howItWorks.text3Strong1')}</strong>
              {t('aboutUs.sections.howItWorks.text3Part2')}{' '}
              <strong>{t('aboutUs.sections.howItWorks.text3Strong2')}</strong>
              {t('aboutUs.sections.howItWorks.text3Part3')}
            </p>
            <p className="flex items-center gap-2 pt-5 text-m font-bold text-[#2E7A32]">
              <ArrowRight className="h-6 w-6" aria-hidden={true} />
              {t('aboutUs.sections.howItWorks.efficiency')}
            </p>
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
      />
      <PictureContentLeft
        bgColor="#FFFFFF"
        srcImg={Aggregation}
        titleColor={'#2E7A32'}
        title={t('aboutUs.sections.equitableAccess.title')}
        content={
          <div>
            {t('aboutUs.sections.equitableAccess.textPart1')}{' '}
            <strong>{t('aboutUs.sections.equitableAccess.textStrong')}</strong>
            {t('aboutUs.sections.equitableAccess.textPart2')}
          </div>
        }
        contentColor="#000000"
      />
      <PictureContentRight
        bgColor="#FFFFFF"
        srcImg={getImage('sliCapacity')}
        titleColor={'#2E7A32'}
        title={t('aboutUs.sections.capacity.title')}
        content={
          <div>
            <p>
              {t('aboutUs.sections.capacity.text1Part1')}{' '}
              <strong>{t('aboutUs.sections.capacity.text1Strong')}</strong>
              {t('aboutUs.sections.capacity.text1Part2')}
            </p>
            &nbsp;
            <p>{t('aboutUs.sections.capacity.text2')}</p>
          </div>
        }
        contentColor="#000000"
      />
      <ContentBox
        title={t('aboutUs.sections.strategy.title')}
        content={
          <div>
            {t('aboutUs.sections.strategy.textPart1')}{' '}
            <font className="italic">{t('aboutUs.sections.strategy.textItalic')}</font>
            {t('aboutUs.sections.strategy.textPart2')}{' '}
            <a
              href={t('aboutUs.sections.strategy.link30x30Url')}
              target="30x30"
              className="text-lime-700 hover:text-emerald-700 hover:underline"
            >
              {t('aboutUs.sections.strategy.link30x30Text')}
            </a>
            {t('aboutUs.sections.strategy.textPart3')}
            <p>&nbsp;</p>
          </div>
        }
        bgColor={'#FFFFFF'}
        titleColor={'#2E7A32'}
      />
      {/*<PictureContentRight
          title="Resources"
          titleColor="#2E7A32"
          content={`Lorem ipsum dolor sit amet consectetur adipisicing elit. Voluptatum
             molestiae delectus culpa hic assumenda, voluptate reprehenderit
             dolore autem cum ullam sed odit perspiciatis. Doloribus quos velit,
             eveniet ex deserunt fuga?.
             Lorem ipsum dolor sit amet consectetur adipisicing elit. Voluptatum
             molestiae delectus culpa hic assumenda, voluptate reprehenderit
             dolore autem cum ullam sed odit perspiciatis. Doloribus quos velit,
             eveniet ex deserunt fuga?`}
          srcImg={PictureContent}
          btnContent="Find more"
          btnColor="#A8C356"
          display="inline-block"
        />*/}
    </div>
  );
};

export { AboutUsPage };
